import React, { useState } from 'react';
import {
  Typography,
  Alert,
  Paper,
  Box,
  IconButton,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Button,
  List,
  ListItem,
  ListItemText,
  Card,
  CardContent,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from '@mui/material';
import {
  HelpOutline as HelpOutlineIcon,
  Info as InfoIcon,
  Close as CloseIcon,
  TableChart as TableChartIcon,
  RestartAlt as RestartAltIcon,
  Assessment as AssessmentIcon,
} from '@mui/icons-material';
import ViolationsCharts from '../charts/ViolationsCharts';
import FullScreenTable from '../tables/FullScreenTable';
import UpgradeRequestDialog from '../UpgradeRequestDialog';

const ViolationsTab = ({
  searchData,
  getCurrentTableCounts,
  getCurrentTableData,
  getCurrentTableType,
  violationsTableSort,
  handleViolationsTableSort,
  resetViolationsTableSort,
  violationsByRuleTableSort,
  setViolationsByRuleTableSort,
  handleViolationsByRuleTableSort,
  resetViolationsByRuleTableSort,
  violationsFilter,
  setViolationsFilter,
  resetViolationsFilter,
  hiddenViolationsLines,
  setHiddenViolationsLines,
  resetHiddenViolationsLines,
  hiddenReinspectionLines,
  setHiddenReinspectionLines,
  resetHiddenReinspectionLines,
  tierInfo,
}) => {
  // Handler for violations by rule table sorting
  const handleViolationsByRuleSort = (field) => {
    handleViolationsByRuleTableSort(field);
  };

  // Reset function for violations by rule table
  const handleResetViolationsByRule = () => {
    resetViolationsByRuleTableSort();
  };
  
  // Local state for operator names popup
  const [operatorNamesDialogOpen, setOperatorNamesDialogOpen] = useState(false);
  const [fullScreenTableOpen, setFullScreenTableOpen] = useState(false);
  const [aboutDialogOpen, setAboutDialogOpen] = useState(false);
  // Local state for upgrade dialog
  const [upgradeDialogOpen, setUpgradeDialogOpen] = useState(false);
  // Don't render if not violations tab or no data
  if (getCurrentTableType() !== 'violations' || !getCurrentTableData() || getCurrentTableData().length === 0) {
    return null;
  }

  const violationsData = getCurrentTableData();
  
  // Calculate unique operator names for this tab only
  const getTabOperatorNames = () => {
    if (!violationsData || violationsData.length === 0) return [];
    
    const operatorNames = new Set();
    violationsData.forEach(record => {
      const operator = record.operator_name || record['Operator Name'] || record.operator;
      if (operator && operator.toString().trim() !== '') {
        operatorNames.add(operator.toString().trim());
      }
    });
    
    return Array.from(operatorNames).sort();
  };
  
  // Calculate total violations
  const totalViolations = violationsData.length;
  
  // Calculate date range
  const dateColumn = violationsData[0].violation_discovery_date !== undefined ? 'violation_discovery_date' : null;
  
  let dateRangeText = '';
  if (dateColumn) {
    const dates = violationsData
      .map(record => record[dateColumn])
      .filter(date => date)
      .map(date => new Date(date))
      .filter(date => !isNaN(date.getTime()));
    
    if (dates.length > 0) {
      const minDate = new Date(Math.min(...dates));
      const maxDate = new Date(Math.max(...dates));
      
      const formatDate = (date) => {
        return date.toLocaleDateString('en-US', { month: 'short', year: 'numeric' });
      };
      
      // Check if dates are in the same month/year
      if (minDate.getFullYear() === maxDate.getFullYear() && minDate.getMonth() === maxDate.getMonth()) {
        dateRangeText = ` (${formatDate(minDate)})`;
      } else {
        dateRangeText = `, with occurances from ${formatDate(minDate)} to ${formatDate(maxDate)}`;
      }
    }
  }
  
  // Calculate unique rules violated - use "violated_rule" column
  const ruleColumn = violationsData[0].violated_rule !== undefined ? 'violated_rule' : null;
  
  let uniqueRules = 0;
  if (ruleColumn) {
    const uniqueRuleSet = new Set(
      violationsData
        .map(record => record[ruleColumn])
        .filter(rule => rule && rule.toString().trim() !== '')
    );
    uniqueRules = uniqueRuleSet.size;
  }
  
  // Calculate major violations - use "major_violation_indicator" column == "Y"
  const majorViolationsColumn = violationsData[0].major_violation_indicator !== undefined ? 'major_violation_indicator' : null;
  
  let majorViolations = 0;
  if (majorViolationsColumn) {
    majorViolations = violationsData.filter(record => {
      const major = record[majorViolationsColumn];
      return major && major.toString().toUpperCase() === 'Y';
    }).length;
  }
  
  // Calculate re-inspections - count rows where "compliant_on_reinspection" != "--"
  const reinspectionColumn = violationsData[0].compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
  
  let reinspections = 0;
  if (reinspectionColumn) {
    reinspections = violationsData.filter(record => {
      const reinspection = record[reinspectionColumn];
      return reinspection && reinspection.toString().trim() !== '' && reinspection.toString().trim() !== '--';
    }).length;
  }
  
  // Calculate compliance rate on re-inspection - count "Y" values in "Compliant on Reinspection" column
  let complianceRate = 0;
  if (reinspectionColumn) {
    const reinspectionRecords = violationsData.filter(record => {
      const reinspection = record[reinspectionColumn];
      return reinspection && reinspection.toString().trim() !== '' && reinspection.toString().trim() !== '--';
    });
    
    if (reinspectionRecords.length > 0) {
      const compliantReinspections = reinspectionRecords.filter(record => {
        const compliance = record[reinspectionColumn];
        return compliance && compliance.toString().toUpperCase() === 'Y';
      }).length;
      complianceRate = (compliantReinspections / reinspectionRecords.length) * 100;
    }
  }

  return (
    <>
      {/* Violations Findings Card */}
      <Card elevation={0} sx={{ mb: 3, border: 1, borderColor: 'divider' }}>
        <CardContent sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <AssessmentIcon sx={{ fontSize: 32, color: 'primary.main', mr: 2 }} />
            <Typography variant="h5" component="h2">
              Violations
            </Typography>
            <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', ml: 'auto' }}>
              <Tooltip title="About Violations" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setAboutDialogOpen(true)}
                >
                  <HelpOutlineIcon />
                </IconButton>
              </Tooltip>
              <Tooltip title="View Raw Data" arrow>
                <IconButton
                  size="small"
                  color="primary"
                  onClick={() => setFullScreenTableOpen(true)}
                  disabled={tierInfo && !tierInfo.can_view_raw_data}
                  sx={{
                    opacity: (tierInfo && !tierInfo.can_view_raw_data) ? 0.5 : 1,
                    cursor: (tierInfo && !tierInfo.can_view_raw_data) ? 'not-allowed' : 'pointer'
                  }}
                >
                  <TableChartIcon />
                </IconButton>
              </Tooltip>
            </Box>
          </Box>
          
          {/* Content */}
          <Box>
            {/* Unique Operator Names */}
            <Box sx={{ mb: 2, textAlign: 'left' }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                <Typography>
                  Unique Operator Names — {getTabOperatorNames().length.toLocaleString()}
                </Typography>
                <Tooltip title="View all unique operator names" arrow>
                  <IconButton
                    size="small"
                    color="primary"
                    onClick={() => setOperatorNamesDialogOpen(true)}
                  >
                    <InfoIcon />
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>
            
            {/* Divider */}
            <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 1, mt: 2 }} />
            
            {/* Findings Content */}
            <Box sx={{ mt: 2 }}>
              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The violations data review process discovered{' '}
                <strong>{totalViolations.toLocaleString()}</strong> violation{totalViolations !== 1 ? 's' : ''} associated with the subject assets{dateRangeText}. Of the discovered violation{totalViolations !== 1 ? 's' : ''},{' '}
                <strong>{uniqueRules}</strong> different rule{uniqueRules !== 1 ? 's' : ''} identified as violated.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                The review further identified{' '}
                <strong>{majorViolations.toLocaleString()}</strong> major violation{majorViolations !== 1 ? 's' : ''} associated with the subject assets over the same period.
              </Typography>

              <Typography variant="body1" sx={{ lineHeight: 1.6, mb: 3 }}>
                Inspectors conducted{' '}
                <strong>{reinspections.toLocaleString()}</strong> reinspection{reinspections !== 1 ? 's' : ''} over the same period{reinspections > 0 ? `, with ` : ''}{reinspections > 0 ? <strong>{complianceRate.toFixed(1)}%</strong> : ''}{reinspections > 0 ? ` compliance rate on reinspection` : ''}.
              </Typography>

              {/* Analytics Scope Notice */}
              {getCurrentTableData().length < getCurrentTableCounts() && (
                <Alert severity="info" sx={{ mb: 3 }}>
                  <Typography variant="body2">
                    <strong>Note:</strong> Analytics are based on {getCurrentTableData().length.toLocaleString()} of {getCurrentTableCounts().toLocaleString()} identified records 
                    (limited to first {getCurrentTableData().length.toLocaleString()} rows).
                  </Typography>
                </Alert>
              )}

              {/* Violations by Rule Table */}
              {(() => {
                const violationsData = getCurrentTableData();
                
                // Group violations by rule
                const ruleGroups = {};
                
                violationsData.forEach(record => {
                  const ruleColumn = record.violated_rule_description !== undefined ? 'violated_rule_description' : null;
                  
                  if (ruleColumn && record[ruleColumn]) {
                    const rule = record[ruleColumn].toString().trim();
                    
                    if (!ruleGroups[rule]) {
                      ruleGroups[rule] = {
                        rule: rule,
                        violations: 0,
                        majorViolations: 0,
                        reinspections: 0,
                        compliantReinspections: 0
                      };
                    }
                    
                    ruleGroups[rule].violations++;
                    
                    // Check for major violations
                    const majorViolationsColumn = record.major_violation_indicator !== undefined ? 'major_violation_indicator' : null;
                    
                    if (majorViolationsColumn && record[majorViolationsColumn] && 
                        record[majorViolationsColumn].toString().toUpperCase() === 'Y') {
                      ruleGroups[rule].majorViolations++;
                    }
                    
                    // Check for reinspections
                    const reinspectionColumn = record.compliant_on_reinspection !== undefined ? 'compliant_on_reinspection' : null;
                    
                    if (reinspectionColumn && record[reinspectionColumn] && 
                        record[reinspectionColumn].toString().trim() !== '' && 
                        record[reinspectionColumn].toString().trim() !== '--') {
                      ruleGroups[rule].reinspections++;
                      
                      if (record[reinspectionColumn].toString().toUpperCase() === 'Y') {
                        ruleGroups[rule].compliantReinspections++;
                      }
                    }
                  }
                });
                
                // Convert to array and apply sorting
                let tableData = Object.values(ruleGroups)
                  .map(data => ({
                    ...data,
                    percentageOfTotal: totalViolations > 0 ? (data.violations / totalViolations) * 100 : 0,
                    complianceRate: data.reinspections > 0 ? (data.compliantReinspections / data.reinspections) * 100 : 0
                  }));

                // Apply sorting
                if (violationsByRuleTableSort.field) {
                  tableData.sort((a, b) => {
                    let aVal = a[violationsByRuleTableSort.field];
                    let bVal = b[violationsByRuleTableSort.field];
                    
                    if (violationsByRuleTableSort.direction === 'desc') {
                      [aVal, bVal] = [bVal, aVal];
                    }
                    
                    if (typeof aVal === 'string') {
                      return aVal.localeCompare(bVal);
                    }
                    return aVal - bVal;
                  });
                } else {
                  // Default sort by violations count (descending)
                  tableData.sort((a, b) => b.violations - a.violations);
                }
                
                return (
                  <>
                    <Box sx={{ mb: 2, display: 'flex', justifyContent: 'space-between', alignItems: 'center', mt: 3 }}>
                      <Typography variant="h6" color="text.secondary">
                        Violations by Rule
                      </Typography>
                      <Tooltip title="Reset Table" arrow>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={handleResetViolationsByRule}
                        >
                          <RestartAltIcon />
                        </IconButton>
                      </Tooltip>
                    </Box>
                    <TableContainer component={Paper} elevation={0} variant="outlined" sx={{ mt: 2, border: 1, borderColor: 'divider' }} data-table-title="Violations by Rule" data-table-type="findings">
                      <Table size="small">
                        <TableHead>
                          <TableRow>
                            <TableCell 
                              sx={{ 
                                fontWeight: 600, 
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => handleViolationsByRuleSort('rule')}
                            >
                              Violated Rule
                              {violationsByRuleTableSort.field === 'rule' && (
                                <span style={{ marginLeft: '4px' }}>
                                  {violationsByRuleTableSort.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 600, 
                                textAlign: 'right',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => handleViolationsByRuleSort('violations')}
                            >
                              Count
                              {violationsByRuleTableSort.field === 'violations' && (
                                <span style={{ marginLeft: '4px' }}>
                                  {violationsByRuleTableSort.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell 
                              sx={{ 
                                fontWeight: 600, 
                                textAlign: 'right',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => handleViolationsByRuleSort('percentageOfTotal')}
                            >
                              % of Total
                              {violationsByRuleTableSort.field === 'percentageOfTotal' && (
                                <span style={{ marginLeft: '4px' }}>
                                  {violationsByRuleTableSort.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ 
                                fontWeight: 600, 
                                textAlign: 'right',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => handleViolationsByRuleSort('majorViolations')}
                            >
                              Major
                              {violationsByRuleTableSort.field === 'majorViolations' && (
                                <span style={{ marginLeft: '4px' }}>
                                  {violationsByRuleTableSort.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ 
                                fontWeight: 600, 
                                textAlign: 'right',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => handleViolationsByRuleSort('reinspections')}
                            >
                              Reinspections
                              {violationsByRuleTableSort.field === 'reinspections' && (
                                <span style={{ marginLeft: '4px' }}>
                                  {violationsByRuleTableSort.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </TableCell>
                            <TableCell
                              sx={{ 
                                fontWeight: 600, 
                                textAlign: 'right',
                                cursor: 'pointer',
                                '&:hover': { backgroundColor: 'rgba(0, 0, 0, 0.04)' }
                              }}
                              onClick={() => handleViolationsByRuleSort('complianceRate')}
                            >
                              % Compliant
                              {violationsByRuleTableSort.field === 'complianceRate' && (
                                <span style={{ marginLeft: '4px' }}>
                                  {violationsByRuleTableSort.direction === 'asc' ? '↑' : '↓'}
                                </span>
                              )}
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {tableData.map((row, index) => (
                            <TableRow key={index}>
                              <TableCell>{row.rule}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{row.violations.toLocaleString()}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{row.percentageOfTotal.toFixed(1)}%</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{row.majorViolations.toLocaleString()}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>{row.reinspections.toLocaleString()}</TableCell>
                              <TableCell sx={{ textAlign: 'right' }}>
                                {row.reinspections > 0 ? `${row.complianceRate.toFixed(1)}%` : 'N/A'}
                              </TableCell>
                            </TableRow>
                          ))}
                          
                          {/* Grand Total Row */}
                          <TableRow sx={{ backgroundColor: 'grey.100' }}>
                            <TableCell sx={{ fontWeight: 'bold' }}>Grand Total</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{totalViolations.toLocaleString()}</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>100.0%</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{majorViolations.toLocaleString()}</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>{reinspections.toLocaleString()}</TableCell>
                            <TableCell sx={{ textAlign: 'right', fontWeight: 'bold' }}>
                              {reinspections > 0 ? `${complianceRate.toFixed(1)}%` : 'N/A'}
                            </TableCell>
                          </TableRow>
                        </TableBody>
                      </Table>
                    </TableContainer>
          </>
        );
      })()}
            </Box>
          </Box>
          
          {tierInfo && !tierInfo.can_view_raw_data && (
            <Typography variant="body2" color="primary.main" sx={{ 
              mt: 2, 
              mb: 1, 
              fontWeight: 500 
            }}>
              💡 <button 
                onClick={() => setUpgradeDialogOpen(true)}
                style={{ 
                  color: 'inherit', 
                  textDecoration: 'underline',
                  cursor: 'pointer',
                  background: 'none',
                  border: 'none',
                  padding: 0,
                  font: 'inherit'
                }}
              >
                Upgrade to Pro to view raw data tables
              </button>
            </Typography>
          )}
        </CardContent>
      </Card>

      {/* Violations Charts */}
      <ViolationsCharts
        violationsData={getCurrentTableData()}
        onSortViolationsNotCompliant={handleViolationsTableSort}
        violationsNotCompliantSortField={violationsTableSort.field}
        violationsNotCompliantSortDirection={violationsTableSort.direction}
        onResetViolationsNotCompliant={resetViolationsTableSort}
        violationsFilter={violationsFilter}
        setViolationsFilter={setViolationsFilter}
        resetViolationsFilter={resetViolationsFilter}
        hiddenViolationsLines={hiddenViolationsLines}
        setHiddenViolationsLines={setHiddenViolationsLines}
        resetHiddenViolationsLines={resetHiddenViolationsLines}
        hiddenReinspectionLines={hiddenReinspectionLines}
        setHiddenReinspectionLines={setHiddenReinspectionLines}
        resetHiddenReinspectionLines={resetHiddenReinspectionLines}
      />

      {/* About Dialog */}
      <Dialog
        open={aboutDialogOpen}
        onClose={() => setAboutDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          About Violations
        </DialogTitle>
        <DialogContent>
          <DialogContentText>
            <Typography paragraph>
              Except for certain violations that may be resolved quickly through a phone call, state regulators formally notify the operator of a violation in writing. Notification of a violation will specify a deadline for compliance. Shortly after the deadline for compliance, the inspector returns to the location to verify compliance.
            </Typography>
            <Typography paragraph>
              When violations are discovered during inspections, through operator self-reports, or through other means, state regulators initiate enforcement actions. These actions may include notices of violation, administrative penalties, or other enforcement measures depending on the severity and nature of the violation.
            </Typography>
            <Typography paragraph>
              <strong>Violated Rule</strong> – The statewide rule found to be non-compliant during the inspection and certified as non-compliant in the technical review of the inspection.
            </Typography>
            <Typography paragraph>
              <strong>Major Violation</strong> – Indicates if the violation was classified as a major violation during the regulatory technical review of the inspection.
            </Typography>
            <Typography paragraph>
              <strong>Compliant on Reinspection</strong> – Indicates if the violation was found to be compliant in subsequent backcheck inspections (i.e., follow-up inspections). "--" indicates that a backcheck inspection has not been performed for this violation.
            </Typography>
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAboutDialogOpen(false)} color="primary">
            Close
          </Button>
        </DialogActions>
      </Dialog>

      {/* Operator Names Dialog */}
      <Dialog
        open={operatorNamesDialogOpen}
        onClose={() => setOperatorNamesDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Typography variant="h6">
              Unique Operator Names
            </Typography>
            <IconButton
              onClick={() => setOperatorNamesDialogOpen(false)}
              size="small"
            >
              <CloseIcon />
            </IconButton>
          </Box>
        </DialogTitle>
        <DialogContent>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Showing unique operator names from current search results ({getCurrentTableData().length.toLocaleString()} records)
          </Typography>
          <Box sx={{ maxHeight: 400, overflow: 'auto' }}>
            {getTabOperatorNames().length > 0 ? (
              <Box>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  <strong>Total: {getTabOperatorNames().length.toLocaleString()} unique operators</strong>
                </Typography>
                <List dense>
                  {getTabOperatorNames().map((operator, index) => (
                    <ListItem key={index} sx={{ py: 0.5 }}>
                      <ListItemText primary={operator} />
                    </ListItem>
                  ))}
                </List>
              </Box>
            ) : (
              <Typography>No operator data available</Typography>
            )}
          </Box>
        </DialogContent>
      </Dialog>

      {/* Full Screen Table Dialog */}
      <FullScreenTable
        open={fullScreenTableOpen}
        onClose={() => setFullScreenTableOpen(false)}
        tableType="violations"
        data={violationsData}
        allColumns={violationsData.length > 0 ? Object.keys(violationsData[0]) : []}
        defaultColumns={['operator_name', 'lease_facility_name', 'violation_discovery_date', 'violated_rule_description']}
        totalCount={getCurrentTableCounts()}
        rowLimit={tierInfo?.table_limits?.violations || 500}
        onReset={() => {
          // Reset any violations-specific state if needed
        }}
      />
      
      {/* Upgrade Request Dialog */}
      <UpgradeRequestDialog 
        open={upgradeDialogOpen}
        onClose={() => setUpgradeDialogOpen(false)}
      />
    </>
  );
};

export default ViolationsTab;